/**
 * CustomerPaymentsTab - Payments tab content for customer detail modal
 * Extracted from CustomersPage.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  sent: { icon: '✅', label: 'Gesendet', cls: 'text-emerald-700' },
  failed: { icon: '❌', label: 'Fehlgeschlagen', cls: 'text-red-700' },
  manual_fallback: { icon: '📲', label: 'Manuell (WhatsApp Web)', cls: 'text-amber-700' },
};

const CustomerPaymentsTab = ({
  paymentTokens,
  handleCreatePaymentToken,
  setSendLinkDialogOpen,
  handleDeletePaymentToken,
  customerId
}) => {
  const [sendLog, setSendLog] = useState({ logs: [], total: 0 });

  const loadSendLog = useCallback(async () => {
    if (!customerId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/payrexx/customers/${customerId}/payment-tokens/send-log`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSendLog(res.data || { logs: [], total: 0 });
    } catch (e) {
      // Log-Anzeige ist optional — Tab trotzdem anzeigen
    }
  }, [customerId]);

  useEffect(() => { loadSendLog(); }, [loadSendLog]);

  return (
    <div className="space-y-6" data-testid="customer-payments-tab">
      {/* Payment Methods Section (Payrexx Kreditkarten) */}
      <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-testid="payments-methods-section">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h4 className="font-semibold text-green-900">Hinterlegte Kreditkarten (Payrexx)</h4>
            <p className="text-sm text-green-700">Für wiederkehrende Zahlungen über Payrexx</p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSendLinkDialogOpen(true)}
              className="bg-blue-50 hover:bg-blue-100"
              data-testid="payment-token-send-link"
            >
              📧📱 Link senden
            </Button>
          </div>
        </div>
        
        {/* Show payment tokens */}
        {paymentTokens.length > 0 ? (
          <div className="space-y-2" data-testid="payment-token-list">
            {paymentTokens.map((token) => {
              // Format expiry date nicely
              let expiryDisplay = '';
              if (token.expiration_month && token.expiration_year) {
                expiryDisplay = `${String(token.expiration_month).padStart(2, '0')}/${token.expiration_year}`;
              } else if (token.card_expiry) {
                // card_expiry can be "2028-09" or "09/28"
                if (token.card_expiry.includes('-')) {
                  const parts = token.card_expiry.split('-');
                  expiryDisplay = `${parts[1]}/${parts[0]}`;
                } else {
                  expiryDisplay = token.card_expiry;
                }
              }
              
              return (
                <div key={token.id} className="flex justify-between items-center bg-white p-3 rounded border border-green-300" data-testid={`payment-token-${token.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💳</span>
                    <div>
                      <div className="font-semibold">{token.card_brand || 'Kreditkarte'} •••• {token.last_four_digits || token.card_last_four}</div>
                      <div className="text-sm text-gray-600">
                        {expiryDisplay ? `Gültig bis: ${expiryDisplay}` : 'Ablaufdatum nicht verfügbar'}
                        {token.cardholder_name && ` • ${token.cardholder_name}`}
                      </div>
                      {token.is_primary && (
                        <Badge variant="success" className="text-xs mt-1">Standard</Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeletePaymentToken(token.id)}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`payment-token-delete-${token.id}`}
                  >
                    🗑️ Löschen
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-green-600 bg-white p-3 rounded border border-green-200" data-testid="payment-token-empty">
            Keine Kreditkarte hinterlegt. Bitte hinterlegen Sie eine Kreditkarte für wiederkehrende Zahlungen.
          </div>
        )}
      </div>

      {/* 08/2026: Versand-Historie der "Karte hinterlegen"-Links */}
      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="card-link-send-log">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h4 className="font-semibold text-slate-800">Zahlungslink-Versand</h4>
            <p className="text-xs text-slate-500">
              {sendLog.total > 0
                ? `${sendLog.total}× versendet/versucht — letzte ${Math.min(sendLog.logs.length, 20)} Einträge`
                : 'Noch kein Zahlungslink versendet'}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={loadSendLog} title="Aktualisieren" data-testid="card-link-send-log-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        {sendLog.logs.length > 0 && (
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {sendLog.logs.map((log) => {
              const meta = STATUS_META[log.status] || { icon: '•', label: log.status, cls: 'text-slate-600' };
              return (
                <div key={log.id} className="flex items-start gap-2 bg-white rounded border border-slate-200 px-3 py-2 text-xs" data-testid={`card-link-log-${log.id}`}>
                  <span className="text-base leading-none">{log.method === 'email' ? '📧' : '📱'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-semibold ${meta.cls}`}>{meta.icon} {meta.label}</span>
                      <span className="text-slate-500">{log.method === 'email' ? 'E-Mail' : 'WhatsApp'}</span>
                      <span className="text-slate-400">
                        {new Date(log.sent_at).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {log.sent_by && <span className="text-slate-400">von {log.sent_by}</span>}
                    </div>
                    {log.detail && (
                      <div className={`mt-0.5 truncate ${log.status === 'failed' ? 'text-red-600' : 'text-slate-500'}`} title={log.detail}>
                        {log.detail}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* "Wiederkehrendes Punkte-Abo" wurde entfernt (06/2026):
          Abo-Verwaltung (kündigen/pausieren) gehört in den Konto-Tab,
          Neuverkauf erfolgt ausschliesslich über Sales. */}
    </div>
  );
};

export default React.memo(CustomerPaymentsTab);
